import { useCallback, useEffect, useRef, useState } from 'react'

import { useAuth } from '../lib/auth'

export type UseLegalAgreementsApiData = unknown

export interface UseLegalAgreementsApiState {
  error: unknown
  loading: boolean
  data: UseLegalAgreementsApiData
}

export interface OnAgreementAcceptedProps {
  agreementId: string
  userInput?: Record<string, unknown>
  evidence?: unknown
}

export interface OnAgreementAccepted {
  (props: OnAgreementAcceptedProps): void
}

export interface OnAgreementAcceptedAsync {
  (props: OnAgreementAcceptedProps): Promise<UseLegalAgreementsApiData>
}

export interface OnCoveredEntityUpdatedProps {
  hipaaCoveredEntity: boolean
}

export interface OnCoveredEntityUpdated {
  (props: OnCoveredEntityUpdatedProps): void
}

export interface OnCoveredEntityUpdatedAsync {
  (props: OnCoveredEntityUpdatedProps): Promise<UseLegalAgreementsApiData>
}

export interface UseLegalAgreementsApiResult extends UseLegalAgreementsApiState {
  onAgreementAccepted: OnAgreementAccepted
  onAgreementAcceptedAsync: OnAgreementAcceptedAsync
  onCoveredEntityUpdated: OnCoveredEntityUpdated
  onCoveredEntityUpdatedAsync: OnCoveredEntityUpdatedAsync
}

const useLegalAgreementsApi = (): UseLegalAgreementsApiResult => {
  const isMounted = useRef(true)
  const { getAccessTokenSilently } = useAuth()
  const [state, setState] = useState<UseLegalAgreementsApiState>({
    error: null,
    loading: false,
    data: null,
  })

  useEffect(() => {
    return () => {
      isMounted.current = false
    }
  }, [])

  const sendRequest = useCallback(
    async (requestBody): Promise<UseLegalAgreementsApiData> => {
      let data: unknown

      try {
        setState({ error: null, loading: true, data: null })
        const accessToken = await getAccessTokenSilently()
        const response = await fetch('/api/agreements', {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(requestBody),
        })
        data = await response.json()
      } catch (error) {
        setState({
          loading: false,
          error,
          data: null,
        })
        throw error
      }

      // refresh token
      return Promise.resolve()
        .then(async () => {
          await getAccessTokenSilently({ ignoreCache: true })
          if (isMounted.current) {
            setState({
              data,
              error: null,
              loading: false,
            })
          }
          return data
        })
        .catch((error) => {
          setState({
            loading: false,
            error,
            data: null,
          })
          throw error
        })
    },
    [getAccessTokenSilently],
  )

  const onAgreementAcceptedAsync = useCallback<OnAgreementAcceptedAsync>(
    ({ agreementId, evidence, userInput }) =>
      sendRequest({
        acceptedAgreement: {
          id: agreementId,
          evidence,
          ...(userInput && {
            userInput,
          }),
        },
      }),
    [sendRequest],
  )

  const onAgreementAccepted = useCallback<OnAgreementAccepted>(
    (props) => {
      onAgreementAcceptedAsync(props).catch((error) => {
        console.error(error)
      })
    },
    [onAgreementAcceptedAsync],
  )

  const onCoveredEntityUpdatedAsync = useCallback<OnCoveredEntityUpdatedAsync>(
    ({ hipaaCoveredEntity }) =>
      sendRequest({
        hipaaCoveredEntity,
      }),
    [sendRequest],
  )

  const onCoveredEntityUpdated = useCallback<OnCoveredEntityUpdated>(
    (props) => {
      onCoveredEntityUpdatedAsync(props).catch((error) => {
        console.error(error)
      })
    },
    [onCoveredEntityUpdatedAsync],
  )

  return {
    ...state,
    onAgreementAccepted,
    onAgreementAcceptedAsync,
    onCoveredEntityUpdated,
    onCoveredEntityUpdatedAsync,
  }
}

export default useLegalAgreementsApi
